"use client";

import { useEffect, useRef, useState } from "react";

export default function PromptModal({
  title,
  initialValue = "",
  placeholder = "",
  readOnly = false,
  confirmLabel = "OK",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
}) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const titleId = `prompt-modal-title-${useRef(Math.random().toString(36).slice(2)).current}`;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      if (readOnly) inputRef.current.select();
    }
    const handleKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      } else if (e.key === "Tab") {
        const focusable = [inputRef.current, cancelBtnRef.current, confirmBtnRef.current].filter(Boolean);
        if (focusable.length < 2) return;
        const idx = focusable.indexOf(document.activeElement);
        e.preventDefault();
        if (e.shiftKey) {
          focusable[idx <= 0 ? focusable.length - 1 : idx - 1].focus();
        } else {
          focusable[idx === focusable.length - 1 || idx === -1 ? 0 : idx + 1].focus();
        }
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onCancel, readOnly]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(readOnly ? initialValue : value);
  };

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "transparent",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-6)",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          maxWidth: 480,
          width: "100%",
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-lg)",
          padding: "var(--space-6)",
          textAlign: "left",
        }}
      >
        <h2
          id={titleId}
          style={{
            fontSize: "var(--font-size-lg)",
            fontWeight: "var(--font-weight-semibold)",
            lineHeight: "var(--line-height-snug)",
            marginBottom: "var(--space-4)",
            color: "var(--color-text)",
          }}
        >
          {title}
        </h2>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          style={{
            display: "block",
            width: "100%",
            padding: "var(--space-2) var(--space-3)",
            marginBottom: "var(--space-5)",
            fontSize: "var(--font-size-sm)",
            fontFamily: readOnly ? "var(--font-mono)" : "var(--font-primary)",
            border: "1px solid var(--color-border-light)",
            borderRadius: "var(--radius-sm)",
            background: readOnly ? "var(--color-bg-elevated)" : "var(--color-bg)",
            color: "var(--color-text)",
            outline: "none",
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-2)" }}>
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            style={{
              padding: "var(--space-2) var(--space-4)",
              background: "transparent",
              color: "var(--color-text-muted)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-medium)",
              fontFamily: "var(--font-primary)",
              cursor: "pointer",
            }}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="submit"
            style={{
              padding: "var(--space-2) var(--space-4)",
              background: "var(--color-text)",
              color: "var(--color-bg)",
              border: "none",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              fontFamily: "var(--font-primary)",
              cursor: "pointer",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
